import type { Room } from '@/types/room';

// Canvas: 920 × 420 px
// Row 1 (y=10, h=120): 5 department rooms
// Gap (y=130-150): 20px corridor
// Row 2 (y=150, h=120): 5 department rooms
// Gap (y=270-290): 20px corridor
// Row 3 (y=290, h=120): 4 store rooms

const R1_Y = 10;
const R2_Y = 150;
const R3_Y = 290;
const ROW_H = 120;

// Row 1 & 2: 5 rooms of 172px wide with 10px gaps, 10px margin each side
// 10 + 172 + 10 + 172 + 10 + 172 + 10 + 172 + 10 + 172 + 10 = 10 + 5*172 + 5*10 = 10 + 860 + 50 = 920 ✓
const DEPT_W = 172;
const DEPT_COLS = [10, 192, 374, 556, 738];

// Row 3: 4 stores of 217px each with 10px gaps
// 10 + 217 + 10 + 217 + 10 + 217 + 10 + 219 + 10 = 920
const STORE_W = 217;
const STORE_LAST_W = 219;
const STORE_COLS = [10, 237, 464, 691];

function makeWaypoints(x: number, y: number, w: number, h: number, id: string) {
  return [
    { id: `${id}_wp1`, x: x + Math.round(w * 0.28), y: y + Math.round(h * 0.38), label: 'Poste 1', occupiedBy: null },
    { id: `${id}_wp2`, x: x + Math.round(w * 0.68), y: y + Math.round(h * 0.38), label: 'Poste 2', occupiedBy: null },
    { id: `${id}_wp3`, x: x + Math.round(w * 0.48), y: y + Math.round(h * 0.72), label: 'Poste 3', occupiedBy: null },
  ];
}

export const ROOMS: Room[] = [
  // ─── Row 1: Departments ──────────────────────────────────────────────
  {
    id: 'dir',
    name: 'Direction Générale',
    label: 'DIRECTION',
    type: 'department',
    color: '#FFD700',
    pixelX: DEPT_COLS[0], pixelY: R1_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[0], R1_Y, DEPT_W, ROW_H, 'dir'),
    adjacentRoomIds: ['mkt', 'cat'],
    maxOccupancy: 3,
    primaryAgentId: 'atlas',
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'mkt',
    name: 'Marketing & Growth',
    label: 'MARKETING',
    type: 'department',
    color: '#E040FB',
    pixelX: DEPT_COLS[1], pixelY: R1_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[1], R1_Y, DEPT_W, ROW_H, 'mkt'),
    adjacentRoomIds: ['dir', 'seo', 'sav'],
    maxOccupancy: 3,
    primaryAgentId: 'nova',
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'seo',
    name: 'SEO & Contenu',
    label: 'SEO',
    type: 'department',
    color: '#00E5FF',
    pixelX: DEPT_COLS[2], pixelY: R1_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[2], R1_Y, DEPT_W, ROW_H, 'seo'),
    adjacentRoomIds: ['mkt', 'com', 'mtg'],
    maxOccupancy: 3,
    primaryAgentId: 'cipher',
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'com',
    name: 'Communication',
    label: 'COMM',
    type: 'department',
    color: '#00E676',
    pixelX: DEPT_COLS[3], pixelY: R1_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[3], R1_Y, DEPT_W, ROW_H, 'com'),
    adjacentRoomIds: ['seo', 'qa', 'dat'],
    maxOccupancy: 3,
    primaryAgentId: 'echo',
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'qa',
    name: 'QA & Contrôle',
    label: 'QA',
    type: 'department',
    color: '#FF1744',
    pixelX: DEPT_COLS[4], pixelY: R1_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[4], R1_Y, DEPT_W, ROW_H, 'qa'),
    adjacentRoomIds: ['com', 'mkp'],
    maxOccupancy: 3,
    primaryAgentId: 'argus',
    storeId: null,
    agentsPresent: [],
  },

  // ─── Row 2: Departments ──────────────────────────────────────────────
  {
    id: 'cat',
    name: 'Catalogue Produit',
    label: 'CATALOGUE',
    type: 'department',
    color: '#FF6D00',
    pixelX: DEPT_COLS[0], pixelY: R2_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[0], R2_Y, DEPT_W, ROW_H, 'cat'),
    adjacentRoomIds: ['dir', 'sav', 's1'],
    maxOccupancy: 3,
    primaryAgentId: 'forge',
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'sav',
    name: 'Relation Client',
    label: 'SAV',
    type: 'department',
    color: '#2979FF',
    pixelX: DEPT_COLS[1], pixelY: R2_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[1], R2_Y, DEPT_W, ROW_H, 'sav'),
    adjacentRoomIds: ['cat', 'mkt', 'mtg', 's1', 's2'],
    maxOccupancy: 3,
    primaryAgentId: 'halo',
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'mtg',
    name: 'Salle de Réunion',
    label: 'RÉUNION',
    type: 'common',
    color: '#7C4DFF',
    pixelX: DEPT_COLS[2], pixelY: R2_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[2], R2_Y, DEPT_W, ROW_H, 'mtg'),
    adjacentRoomIds: ['sav', 'seo', 'dat', 's2', 's3'],
    maxOccupancy: 6,
    primaryAgentId: null,
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'dat',
    name: 'Data & Analytics',
    label: 'DATA',
    type: 'department',
    color: '#7C4DFF',
    pixelX: DEPT_COLS[3], pixelY: R2_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[3], R2_Y, DEPT_W, ROW_H, 'dat'),
    adjacentRoomIds: ['mtg', 'com', 'mkp', 's3', 's4'],
    maxOccupancy: 3,
    primaryAgentId: 'prism',
    storeId: null,
    agentsPresent: [],
  },
  {
    id: 'mkp',
    name: 'Marketplace',
    label: 'MARKET',
    type: 'department',
    color: '#FFB300',
    pixelX: DEPT_COLS[4], pixelY: R2_Y, pixelW: DEPT_W, pixelH: ROW_H,
    waypoints: makeWaypoints(DEPT_COLS[4], R2_Y, DEPT_W, ROW_H, 'mkp'),
    adjacentRoomIds: ['dat', 'qa', 's4'],
    maxOccupancy: 3,
    primaryAgentId: 'vector',
    storeId: null,
    agentsPresent: [],
  },

  // ─── Row 3: Store Rooms ───────────────────────────────────────────────
  {
    id: 's1',
    name: 'Affiches Personnalisées',
    label: 'AFFICHES',
    type: 'store',
    color: '#E040FB',
    pixelX: STORE_COLS[0], pixelY: R3_Y, pixelW: STORE_W, pixelH: ROW_H,
    waypoints: makeWaypoints(STORE_COLS[0], R3_Y, STORE_W, ROW_H, 's1'),
    adjacentRoomIds: ['cat', 'sav', 's2'],
    maxOccupancy: 4,
    primaryAgentId: null,
    storeId: 'store_affiches',
    agentsPresent: [],
  },
  {
    id: 's2',
    name: 'Cadeaux Animaux',
    label: 'ANIMAUX',
    type: 'store',
    color: '#00E676',
    pixelX: STORE_COLS[1], pixelY: R3_Y, pixelW: STORE_W, pixelH: ROW_H,
    waypoints: makeWaypoints(STORE_COLS[1], R3_Y, STORE_W, ROW_H, 's2'),
    adjacentRoomIds: ['s1', 'sav', 'mtg', 's3'],
    maxOccupancy: 4,
    primaryAgentId: null,
    storeId: 'store_animaux',
    agentsPresent: [],
  },
  {
    id: 's3',
    name: 'Citations & Déco',
    label: 'CITATIONS',
    type: 'store',
    color: '#00E5FF',
    pixelX: STORE_COLS[2], pixelY: R3_Y, pixelW: STORE_W, pixelH: ROW_H,
    waypoints: makeWaypoints(STORE_COLS[2], R3_Y, STORE_W, ROW_H, 's3'),
    adjacentRoomIds: ['s2', 'mtg', 'dat', 's4'],
    maxOccupancy: 4,
    primaryAgentId: null,
    storeId: 'store_citations',
    agentsPresent: [],
  },
  {
    id: 's4',
    name: 'Naissance & Famille',
    label: 'FAMILLE',
    type: 'store',
    color: '#FFD700',
    pixelX: STORE_COLS[3], pixelY: R3_Y, pixelW: STORE_LAST_W, pixelH: ROW_H,
    waypoints: makeWaypoints(STORE_COLS[3], R3_Y, STORE_LAST_W, ROW_H, 's4'),
    adjacentRoomIds: ['s3', 'dat', 'mkp'],
    maxOccupancy: 4,
    primaryAgentId: null,
    storeId: 'store_famille',
    agentsPresent: [],
  },
];
